import React from 'react'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import styles from "../charts.module.css"
import { generateColorPalette } from '../../../../utils/utils'
import { formatValueWithUnit } from '../chartUtils'

const RADIAN = Math.PI / 180
const MAX_VISIBLE_LEGENDS = 6
const MAX_VISIBLE_SLICES = 24
const OTHERS_COLOR = "#64748b"
const CATEGORY_COLORS = [
    "#6245ae", "#d2417c", "#0a9c22", "#ff9b32", "#38d1e5", "#8b5cf6",
    "#ef4444", "#14b8a6", "#eab308", "#2563eb", "#f97316", "#22c55e",
    "#a855f7", "#06b6d4", "#f43f5e", "#84cc16", "#6366f1", "#ec4899",
    "#10b981", "#f59e0b", "#3b82f6", "#dc2626", "#7c3aed", "#0891b2"
]

const ChartPie = ({ data, customColors, valueKey = 'value', unit = "" }) => {
    const allDataZero = data.every(item => Number(item[valueKey] || 0) === 0)

    const isOthersItem = (item) => /^outr[oa]s(\s|\(|$)/i.test(`${item?.name || ""}`.trim())

    const positiveData = data.filter(item => Number(item[valueKey] || 0) > 0)
    const regularData = positiveData
        .filter(item => !isOthersItem(item))
        .sort((a, b) => Number(b[valueKey] || 0) - Number(a[valueKey] || 0))
    const existingOthersData = positiveData.filter(isOthersItem)

    const hasGroupedSlices = positiveData.length > MAX_VISIBLE_SLICES
    const visibleSliceCount = hasGroupedSlices ? MAX_VISIBLE_SLICES - 1 : MAX_VISIBLE_SLICES
    const visibleData = regularData.slice(0, visibleSliceCount)
    const groupedData = [
        ...regularData.slice(visibleSliceCount),
        ...existingOthersData,
    ]
    const groupedValue = groupedData.reduce((acc, item) => acc + Number(item[valueKey] || 0), 0)
    const groupedLabel = `Outros (${groupedData.length})`
    const limitedData = groupedData.length > 0
        ? [
            ...visibleData,
            {
                name: groupedLabel,
                [valueKey]: groupedValue,
                groupedItems: groupedData,
                isOthers: true,
            },
        ]
        : visibleData

    const colors = customColors?.length
        ? generateColorPalette(customColors, limitedData.length, { minLightness: 34, maxLightness: 72, variationLightnessStep: 7, hueStep: 42 })
        : generateColorPalette(CATEGORY_COLORS, limitedData.length, { minLightness: 34, maxLightness: 72, variationLightnessStep: 7, hueStep: 42 })

    const chartData = limitedData.map((item, i) => ({
        ...item,
        color: item.isOthers || isOthersItem(item) ? OTHERS_COLOR : item.color || colors[i],
    }))

    const total = chartData.reduce((acc, item) => acc + Number(item[valueKey] || 0), 0)
    const sliceGap = chartData.length > 12 ? 0.25 : 0.6
    const defaultSliceStrokeWidth = chartData.length > 12 ? 1 : 1.5

    const getSlicePercentage = (value) => total > 0 ? (Number(value || 0) / total) * 100 : 0

    const getSliceStrokeWidth = (value) => {
        if (getSlicePercentage(value) < 2) return 0
        return defaultSliceStrokeWidth
    }

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const { name, [valueKey]: value, formattedValue, fill } = payload[0]?.payload || payload[0]
            return (
                <div className={styles.tooltip} style={{ borderLeft: `4px solid ${fill || 'var(--brand-primary)'}`, zIndex: 9999 }}>
                    <p>
                        <span className={styles.tooltipLabel}>{name}</span>
                        <span className={styles.tooltipValue}>{formattedValue || formatValueWithUnit(value, unit)}</span>
                    </p>
                </div>
            )
        }
        return null
    }

    const getPercentage = (value) => {
        return ((total > 0 ? value / total : 0) * 100).toFixed(2) + '%'
    }

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, index }) => {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5
        const x = cx + radius * Math.cos(-midAngle * RADIAN)
        const y = cy + radius * Math.sin(-midAngle * RADIAN)

        const value = chartData[index][valueKey]
        const precisePercent = getSlicePercentage(value)

        if (precisePercent < 6) return null

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor={x > cx ? "start" : "middle"}
                dominantBaseline="central"
                alignmentBaseline="central"
                fontSize={12}
                fontWeight={800}
            >
                {`${getPercentage(value)}`}
            </text>
        )
    }

    const othersLegend = chartData.find(item => item.isOthers || isOthersItem(item))
    const regularLegends = chartData.filter(item => !(item.isOthers || isOthersItem(item)))
    const visibleRegularLegends = othersLegend
        ? regularLegends.slice(0, MAX_VISIBLE_LEGENDS - 1)
        : regularLegends.slice(0, MAX_VISIBLE_LEGENDS)
    const hiddenLegends = othersLegend
        ? regularLegends.slice(MAX_VISIBLE_LEGENDS - 1)
        : regularLegends.slice(MAX_VISIBLE_LEGENDS)
    const hiddenLegendsCount = hiddenLegends.length

    return (
        allDataZero ? (
            <div className={styles.emptyChartMessage}>
                <p>Nenhum dado para exibir</p>
            </div>
        ) : (
            <div className={styles.pieLayout}>
                <div className={styles.pieCanvas}>
                    <ResponsiveContainer width="100%" height="100%" minHeight={150} minWidth={150}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={renderCustomizedLabel}
                                outerRadius="78%"
                                fill="#8884d8"
                                dataKey={valueKey}
                                paddingAngle={sliceGap}
                                stroke="var(--surface-card)"
                                strokeWidth={defaultSliceStrokeWidth}
                                isAnimationActive={false}
                            >
                                {chartData.map((item, index) => (
                                    <Cell
                                        key={`cell-${item.name}-${index}`}
                                        fill={item.color || colors[index % colors.length]}
                                        stroke="var(--surface-card)"
                                        strokeWidth={getSliceStrokeWidth(item[valueKey])}
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} offset={12} wrapperStyle={{ outline: "none" }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className={styles.compactLegend}>
                    {visibleRegularLegends.map((item, index) => (
                        <div className={styles.compactLegendItem} key={`legend-${item.name}-${index}`} title={`${item.name} - ${getPercentage(item[valueKey])}`}>
                            <span className={styles.legendDot} style={{ backgroundColor: item.color || colors[index % colors.length] }} />
                            <span className={styles.legendName}>{item.name}</span>
                            <strong>{getPercentage(item[valueKey])}</strong>
                        </div>
                    ))}
                    {hiddenLegendsCount > 0 && (
                        <div className={styles.compactLegendMore} title={hiddenLegends.map(item => `${item.name} - ${getPercentage(item[valueKey])}`).join("\n")}>
                            +{hiddenLegendsCount} outros
                        </div>
                    )}
                    {othersLegend && (
                        <div className={styles.compactLegendItem} key={`legend-${othersLegend.name}-others`} title={`${othersLegend.name} - ${getPercentage(othersLegend[valueKey])}`}>
                            <span className={styles.legendDot} style={{ backgroundColor: othersLegend.color }} />
                            <span className={styles.legendName}>{othersLegend.name}</span>
                            <strong>{getPercentage(othersLegend[valueKey])}</strong>
                        </div>
                    )}
                </div>
                {hasGroupedSlices && (
                    <div className={styles.chartLimitNote}>
                        Exibindo as {visibleSliceCount} maiores categorias. As demais foram agrupadas em Outros.
                    </div>
                )}
            </div>
        )
    )
}

export default ChartPie
